// hooks/useCustomerHistory.js - IndexedDB 전용 히스토리 관리
import { useEffect, useState, useCallback } from 'react';
import { getKoreanTimeISO } from '../utils/timeUtils';
import {
    openDb,
    idbGetHistoriesByCustomer,
    idbPutHistory,
    idbDeleteHistory,
    migrateAllLocalStorageData
} from '../utils/idb';

// IndexedDB에서 고객 히스토리 가져오기
export const getCustomerHistory = async (customerId) => {
    if (!customerId) return [];
    try {
        await openDb();
        const histories = await idbGetHistoriesByCustomer(customerId, 0, 1000);
        return histories.sort((a, b) => new Date(b.at) - new Date(a.at));
    } catch (e) {
        console.error('히스토리 로드 실패:', e);
        return [];
    }
};

export const getAllCustomerHistory = getCustomerHistory;

// 히스토리 추가
export const addCustomerHistory = async (customerId, entryInput) => {
    if (!customerId) return null;

    try {
        await openDb();

        const entry = {
            id: crypto.randomUUID(),
            customerId: customerId,
            type: entryInput?.type || 'note',
            note: (entryInput?.note || '').trim(),
            at: getKoreanTimeISO(),
            followUp: entryInput?.followUp || null
        };

        await idbPutHistory(entry);
        console.log('히스토리 저장 완료:', entry);

        return entry;
    } catch (e) {
        console.error('히스토리 저장 실패:', e);
        throw e;
    }
};

// 히스토리 수정
export const updateCustomerHistory = async (customerId, entryId, entryInput) => {
    if (!customerId || !entryId) return null;

    try {
        await openDb();

        // 기존 히스토리 가져오기
        const histories = await idbGetHistoriesByCustomer(customerId, 0, 1000);
        const existingEntry = histories.find(h => h.id === entryId);

        if (!existingEntry) {
            throw new Error('히스토리를 찾을 수 없습니다.');
        }

        const updatedEntry = {
            ...existingEntry,
            type: entryInput?.type || existingEntry.type,
            note: (entryInput?.note || '').trim(),
            followUp: entryInput?.followUp !== undefined ? entryInput.followUp : existingEntry.followUp,
            updatedAt: getKoreanTimeISO()
        };

        await idbPutHistory(updatedEntry);
        console.log('히스토리 업데이트 완료:', updatedEntry);

        return updatedEntry;
    } catch (e) {
        console.error('히스토리 업데이트 실패:', e);
        throw e;
    }
};

// 히스토리 삭제
export const deleteCustomerHistory = async (customerId, entryId) => {
    if (!customerId || !entryId) return false;

    try {
        await openDb();
        await idbDeleteHistory(entryId);
        console.log('히스토리 삭제 완료:', entryId);
        return true;
    } catch (e) {
        console.error('히스토리 삭제 실패:', e);
        throw e;
    }
};

export const useCustomerHistory = (customerId) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 히스토리 로드
    const reload = useCallback(async () => {
        if (!customerId) return;

        try {
            setLoading(true);
            setError(null);

            await openDb();
            await migrateAllLocalStorageData();

            const histories = await getCustomerHistory(customerId);
            setHistory(histories);
        } catch (err) {
            console.error('히스토리 로드 실패:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    // 히스토리 추가
    const addHistory = useCallback(async (entryInput) => {
        if (!customerId) return null;

        try {
            const created = await addCustomerHistory(customerId, entryInput);
            if (created) {
                setHistory((prev) => [created, ...prev]);
            }
            return created;
        } catch (err) {
            console.error('히스토리 추가 실패:', err);
            setError(err.message);
            throw err;
        }
    }, [customerId]);

    // 히스토리 수정
    const updateHistory = useCallback(async (entryId, entryInput) => {
        if (!customerId) return null;

        try {
            const updated = await updateCustomerHistory(customerId, entryId, entryInput);
            if (updated) {
                setHistory((prev) => prev.map(entry => entry.id === entryId ? updated : entry));
            }
            return updated;
        } catch (err) {
            console.error('히스토리 수정 실패:', err);
            setError(err.message);
            throw err;
        }
    }, [customerId]);

    // 히스토리 삭제
    const deleteHistory = useCallback(async (entryId) => {
        if (!customerId) return false;

        try {
            const deleted = await deleteCustomerHistory(customerId, entryId);
            if (deleted) {
                setHistory((prev) => prev.filter(entry => entry.id !== entryId));
            }
            return deleted;
        } catch (err) {
            console.error('히스토리 삭제 실패:', err);
            setError(err.message);
            throw err;
        }
    }, [customerId]);

    // 초기 로드
    useEffect(() => {
        reload();
    }, [reload]);

    return {
        history,
        loading,
        error,
        addHistory,
        updateHistory,
        deleteHistory,
        reload
    };
};

// 편의를 위한 초기화 헬퍼 (IndexedDB 전용)
export const ensureHistoryInitialized = async (customerId) => {
    if (!customerId) return;
    try {
        await openDb();
        await migrateAllLocalStorageData();
        // IndexedDB는 자동으로 초기화되므로 별도 작업 불필요
    } catch (e) {
        console.warn('히스토리 초기화 실패:', e);
    }
};


